/**
 * Tapfiliate affiliate code captured from the signup URL.
 *
 * Affiliate links land on the marketing site as `?ref=CODE`. That site records
 * the click, then forwards `ref` onto the app signup link along with campaign
 * and consent params. The Tapfiliate cookie stays on myresellergenie.com, so
 * the app has to read `ref` by name and keep it until the trial is reported.
 *
 * A URL with a new `ref` replaces the saved code. A later page with no `ref`
 * keeps it, so the signup form and the post-signup redirect still have it.
 * Saved codes expire after the same 30-day window as campaign attribution.
 */

export const REFERRAL_STORAGE_KEY = "referralCode";

const REFERRAL_TTL_MS = 30 * 24 * 60 * 60 * 1000;

interface StoredReferral {
  code: string;
  savedAt: number;
}

function isReferralCode(value: string): boolean {
  const code = value.trim();
  return code.length > 0 && code.length <= 128 && !/[&=\s]/.test(code);
}

function writeReferral(code: string): void {
  const stored: StoredReferral = { code, savedAt: Date.now() };
  localStorage.setItem(REFERRAL_STORAGE_KEY, JSON.stringify(stored));
}

/** Forget the saved code once it has been sent with a new account. */
export function clearStoredReferral(): void {
  try {
    localStorage.removeItem(REFERRAL_STORAGE_KEY);
  } catch {
    // localStorage can throw (private mode / blocked); nothing to clear then.
  }
}

/**
 * The code to report to Tapfiliate, or null when nothing is saved or it has
 * expired. Accepts the current `{ code, savedAt }` value and a leftover plain
 * string from older builds, and drops a string that was saved with the rest
 * of the query still attached.
 */
export function getStoredReferralCode(): string | null {
  try {
    const raw = localStorage.getItem(REFERRAL_STORAGE_KEY);
    if (!raw) return null;

    let code: string | null = null;
    let savedAt: number | null = null;
    try {
      const stored = JSON.parse(raw) as Partial<StoredReferral> | null;
      if (stored && typeof stored.code === "string") {
        code = stored.code;
        savedAt = typeof stored.savedAt === "number" ? stored.savedAt : null;
      }
    } catch {
      code = raw;
    }

    if (!code || !isReferralCode(code)) {
      clearStoredReferral();
      return null;
    }
    if (savedAt !== null && Date.now() - savedAt >= REFERRAL_TTL_MS) {
      clearStoredReferral();
      return null;
    }
    return code.trim();
  } catch {
    return null;
  }
}

/**
 * Reads `ref` from the current URL and, when present, replaces the saved
 * code. Returns the stored code (or null). Safe to call on every load.
 */
export function captureReferralFromUrl(
  search: string = typeof window !== "undefined" ? window.location.search : ""
): string | null {
  try {
    const fromUrl = new URLSearchParams(search).get("ref");
    if (fromUrl && isReferralCode(fromUrl)) {
      writeReferral(fromUrl.trim());
      return fromUrl.trim();
    }
    return getStoredReferralCode();
  } catch {
    return getStoredReferralCode();
  }
}
